import { useState } from 'react';
import { FilePlus2, Upload, GitBranch, Package, FolderPlus } from 'lucide-react';
import { useProjectStore } from '../../stores/useProjectStore';
import { CreateProjectModal } from '../projects/CreateProjectModal';
import { ImportProjectModal } from '../projects/ImportProjectModal';
import { CloneGitModal } from '../projects/CloneGitModal';
import styles from './WelcomeWorkspace.module.css';

type ModalType = 'create' | 'import' | 'clone' | 'prefab' | 'blank' | null;

export function WelcomeWorkspace() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const createProject = useProjectStore(s => s.createProject);
  const openProject = useProjectStore(s => s.openProject);

  const handleCreateBlank = async () => {
    const id = await createProject('Untitled Workspace', 'blank');
    await openProject(id);
  };

  const handleCreated = async (projectId: string) => {
    setActiveModal(null);
    await openProject(projectId);
  };

  const actions = [
    {
      icon: <FilePlus2 size={28} />,
      title: 'Create New Project',
      description: 'Start a new Rivellum project with a name and optional template.',
      onClick: () => setActiveModal('create'),
    },
    {
      icon: <Upload size={28} />,
      title: 'Import from Computer',
      description: 'Import files from your local machine into a new workspace.',
      onClick: () => setActiveModal('import'),
    },
    {
      icon: <GitBranch size={28} />,
      title: 'Clone from Git',
      description: 'Clone a Git repository URL into a new project workspace.',
      onClick: () => setActiveModal('clone'),
    },
    {
      icon: <Package size={28} />,
      title: 'Deploy with Prefabs',
      description: 'Scaffold a project from Rivellum prefab templates.',
      onClick: () => setActiveModal('prefab'),
    },
    {
      icon: <FolderPlus size={28} />,
      title: 'Create Blank Workspace',
      description: 'Start with an empty workspace — no files, no templates.',
      onClick: handleCreateBlank,
    },
  ];

  return (
    <div className={styles.welcome}>
      <div className={styles.header}>
        <img src="/logo.png" alt="Rivellum" className={styles.logo} />
        <h1 className={styles.title}>Rivellum Console</h1>
        <p className={styles.subtitle}>
          Create or open a workspace to build Rivellum projects. Network deployment is unavailable because no Rivellum network is currently running.
        </p>
      </div>

      <div className={styles.grid}>
        {actions.map((action, i) => (
          <button
            key={i}
            className={styles.actionCard}
            onClick={action.onClick}
          >
            <div className={styles.actionIcon}>{action.icon}</div>
            <div className={styles.actionContent}>
              <h3 className={styles.actionTitle}>{action.title}</h3>
              <p className={styles.actionDesc}>{action.description}</p>
            </div>
          </button>
        ))}
      </div>

      <div className={styles.footerInfo}>
        Quantum-ready &middot; Encrypted ordering &middot; Post-admission execution
      </div>

      {activeModal === 'create' && (
        <CreateProjectModal onClose={() => setActiveModal(null)} onCreated={handleCreated} />
      )}
      {activeModal === 'import' && (
        <ImportProjectModal onClose={() => setActiveModal(null)} onCreated={handleCreated} />
      )}
      {activeModal === 'clone' && (
        <CloneGitModal onClose={() => setActiveModal(null)} onCreated={handleCreated} />
      )}
      {activeModal === 'prefab' && (
        <CreateProjectModal onClose={() => setActiveModal(null)} onCreated={handleCreated} prefabMode />
      )}
    </div>
  );
}
